/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { GpsSaMapLayer } from '../../../shared/entities/gpsSaMapLayer';
import { Box, LinearProgress, Typography } from '@mui/material';
import appTheme from '../../common/theme/appTheme.json';
import {
  getGpsPositionAccuracyColor,
  getGpsPositionAccuracyLevel,
  GpsPositionAccuracyLevel,
  NACP_MAX_VALUE,
} from '../../../utils/GpsSaUtils';
import styled from 'styled-components';
import { StyledHexagon } from '../map_overlays/GpsSaAccuracyCheckbox';
import { StyledDivider } from '../map_overlays/CommonOverlayComponents';
import { round } from 'lodash';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import { dateToUTCDateFullDayName } from '../../../utils/DateTimeUtils';
import { useSelectorWithMapId } from './BaseMapContainer';
import {
  selectGpsSaMapLayersNormalAccuracyChecked,
  selectGpsSaMapLayersReducedAccuracyChecked,
  selectGpsSaMapLayersLowAccuracyChecked,
} from '../../../store';

const StyledTooltipRowContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

interface GpsSaPositionAccuracyTooltipProps {
  selectedHexagon: GpsSaMapLayer;
}

const GpsSaPositionAccuracyTooltip = (props: GpsSaPositionAccuracyTooltipProps) => {
  const { selectedHexagon } = props;

  // State of the checkboxes for the different accuracy levels
  const normalAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersNormalAccuracyChecked);
  const reducedAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersReducedAccuracyChecked);
  const lowAccuracyChecked = useSelectorWithMapId(selectGpsSaMapLayersLowAccuracyChecked);

  // Returns the accuracy level (Normal, Reduced, Low as an enum) based on the selected hexagon's nacPMode
  const accuracyLevel = useMemo(() => {
    return getGpsPositionAccuracyLevel(selectedHexagon.nacPMode);
  }, [selectedHexagon.nacPMode]);

  // Returns the color associated with the accuracy level which is used throughout the tooltip
  const accuracyColor = useMemo(() => {
    return getGpsPositionAccuracyColor(accuracyLevel);
  }, [accuracyLevel]);

  // Linear mapping of the nacPMode value to a percentage
  const accuracyPercentage = useMemo(() => {
    const percentage = round((selectedHexagon.nacPMode / NACP_MAX_VALUE) * 100, 0);

    // Ensure percentage is within the range of 0 to 100
    return percentage > 100 ? 100 : percentage < 0 ? 0 : percentage;
  }, [selectedHexagon.nacPMode]);

  // Hide the tooltip if the set of hexagons that the tooltip is displayed for is no longer selected
  const shouldHideTooltip = useMemo(() => {
    return (
      (accuracyLevel === GpsPositionAccuracyLevel.Normal && !normalAccuracyChecked) ||
      (accuracyLevel === GpsPositionAccuracyLevel.Reduced && !reducedAccuracyChecked) ||
      (accuracyLevel === GpsPositionAccuracyLevel.Low && !lowAccuracyChecked)
    );
  }, [accuracyLevel, normalAccuracyChecked, reducedAccuracyChecked, lowAccuracyChecked]);

  // If the tooltip should be hidden, return null
  if (shouldHideTooltip) {
    return null;
  }

  return (
    <Box
      id="gps-sa-position-accuracy-tooltip"
      sx={{
        display: 'flex',
        boxSizing: 'content-box',
        width: '183px',
        flexDirection: 'column',
        gap: '10px',
        padding: '8px 12px',
        backgroundColor: appTheme.primaryBackgroundColor,
        border: `1px solid ${appTheme.tailTagBorderColor}`,
        outline: '1px transparent !important',
      }}
    >
      {/* Tooltip Header */}
      <StyledTooltipRowContainer>
        <Typography
          data-testid="TooltipHeaderText"
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0px',
          }}
        >
          {`${accuracyLevel} Accuracy`}
        </Typography>
        <StyledHexagon color={accuracyColor} style={{ transform: 'rotate(0deg)', marginRight: '0px' }} />
      </StyledTooltipRowContainer>

      <StyledDivider sx={{ marginTop: '0px !important', marginBottom: '0px !important' }} />

      {/* Tooltip Position Accuracy Percentage and Progress Bar */}
      <GpsSaPositionAccuracy accuracyColor={accuracyColor} accuracyPercentage={accuracyPercentage} />

      {/* Tooltip Reporting Aircraft within Region component */}
      <GpsSaReportingAircraft aircraftCount={selectedHexagon.aircraftCount} accuracyColor={accuracyColor} />

      {/* Tooltip Last Updated Text */}
      <Typography
        data-testid="LastUpdatedText"
        sx={{
          fontSize: '11px',
          fontWeight: 400,
          lineHeight: '13px',
          letterSpacing: '0px',
          color: appTheme.secondaryHeaderTextColor,
        }}
      >
        {`Updated: ${dateToUTCDateFullDayName(selectedHexagon.generatedAt)}`}
      </Typography>
    </Box>
  );
};

export default GpsSaPositionAccuracyTooltip;

interface GpsSaPositionAccuracyProps {
  accuracyColor: string;
  accuracyPercentage: number;
}

// Section of the GPS SA tooltip that displays the position accuracy percentage and color bar
// Only used within GpsSaPositionAccuracyTooltip, moved to separate component for better readability
const GpsSaPositionAccuracy = (props: GpsSaPositionAccuracyProps) => {
  const { accuracyColor, accuracyPercentage } = props;

  return (
    <Box>
      <StyledTooltipRowContainer>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            letterSpacing: '0px',
          }}
        >
          Position Accuracy
        </Typography>
        <Typography
          data-testid="PositionAccuracyText"
          sx={{
            fontWeight: 700,
            fontSize: '12px',
            letterSpacing: '0px',
            lineHeight: '16px',
            color: accuracyColor,
          }}
        >
          {`${accuracyPercentage}%`}
        </Typography>
      </StyledTooltipRowContainer>
      <LinearProgress
        variant="determinate"
        value={accuracyPercentage}
        sx={{
          height: '8px',
          borderRadius: '4px',
          marginTop: '4px',
          backgroundColor: appTheme.sliderBackgroundColor,
          '& .MuiLinearProgress-bar': {
            backgroundColor: accuracyColor,
            transition: 'none',
          },
        }}
      />
    </Box>
  );
};

interface GpsSaReportingAircraftProps {
  aircraftCount: number;
  accuracyColor: string;
}

// Section of the GPS SA tooltip that displays the number of aircraft reporting in the region
// Only used within GpsSaPositionAccuracyTooltip, moved to separate component for better readability
const GpsSaReportingAircraft = (props: GpsSaReportingAircraftProps) => {
  const { aircraftCount, accuracyColor } = props;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box
        sx={{
          width: '27px',
          height: '24px',
          borderRadius: '3px',
          padding: '2px 4px',
          boxSizing: 'border-box',
          backgroundColor: accuracyColor,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <AirplanemodeActiveIcon sx={{ fontSize: '21px', color: '#000000' }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '12px',
          letterSpacing: '0px',
          marginLeft: '8px',
        }}
      >
        <span data-testid="AircraftCountText">{`${aircraftCount} Aircraft`}</span>
        <br />
        <span style={{ fontWeight: 400 }}>reporting for this region</span>
      </Typography>
    </Box>
  );
};
